import { useTranslation } from "react-i18next";
import { PRODUCT_STATUSES, type ProductStatus } from "@nh/shared";
import { Badge } from "@/shared/ui/core";

export interface ProductStatusBadgeProps {
  status: ProductStatus;
}

/**
 * El estado del producto, con el mismo color en todas partes.
 *
 * El mapa vive aquí y no en cada pantalla: el día que "archivado" cambie
 * de color, cambia en un sitio. Está tipado como `Record<ProductStatus, …>`
 * a propósito — si el contrato añade un estado, esto deja de compilar en
 * vez de pintar una insignia gris sin etiqueta.
 */
const variants: Record<ProductStatus, "success" | "neutral" | "warning"> = {
  published: "success",
  draft: "neutral",
  archived: "warning",
};

const labels: Record<ProductStatus, string> = {
  published: "Publicado",
  draft: "Borrador",
  archived: "Archivado",
};

export function ProductStatusBadge({ status }: ProductStatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <Badge variant={variants[status]} size="sm" dot>
      {t(labels[status])}
    </Badge>
  );
}

/**
 * Las opciones del filtro y del formulario, traducidas.
 *
 * El ORDEN lo pone `PRODUCT_STATUSES` (borrador → publicado → archivado),
 * que es el ciclo de vida real de un producto, y no el orden en que se
 * escribieron las etiquetas de arriba.
 */
export function useProductStatusOptions(): { value: ProductStatus; label: string }[] {
  const { t } = useTranslation();
  return PRODUCT_STATUSES.map((status) => ({
    value: status,
    label: t(labels[status]),
  }));
}
